import { clamp } from "./model.ts";

export type CameraState = { x: number; y: number };
export type ViewportSize = { width: number; height: number };

export const DEFAULT_WORLD_SCALE = 2.35;

export function getWorldSize(viewport: ViewportSize, scale = DEFAULT_WORLD_SCALE) {
  return {
    width: Math.max(1600, Math.round(viewport.width * scale)),
    height: Math.max(1200, Math.round(viewport.height * scale)),
  };
}

/**
 * How far the camera may look past the arena wall.
 *
 * Stopping the camera dead at the world edge pinned a core against the wall to the
 * edge of the *screen*, which is exactly where the HUD is — the radar and the
 * scoreboard in the top corners, the counter along the bottom. Letting the view
 * drift a little outside keeps the core in open screen even when it is against the
 * wall, and seeing a strip of nothing beyond the border is how you can tell it is
 * a border.
 *
 * The bands are proportional to the viewport so the same slice of screen is freed
 * on a laptop and on a 4K display, where the HUD is half again as large.
 */
export function cameraOverscan(viewport: ViewportSize) {
  return {
    side: Math.min(180, viewport.width * 0.1),
    top: Math.min(150, viewport.height * 0.14),
    bottom: Math.min(120, viewport.height * 0.1),
  };
}

export function getCameraTarget(
  focusX: number,
  focusY: number,
  world: ViewportSize,
  viewport: ViewportSize,
): CameraState {
  const room = cameraOverscan(viewport);
  const maxX = Math.max(-room.side, world.width - viewport.width + room.side);
  const maxY = Math.max(-room.top, world.height - viewport.height + room.bottom);
  return {
    x: clamp(focusX - viewport.width * 0.5, -room.side, maxX),
    y: clamp(focusY - viewport.height * 0.5, -room.top, maxY),
  };
}

export function followCamera(
  camera: CameraState,
  focusX: number,
  focusY: number,
  world: ViewportSize,
  viewport: ViewportSize,
  dt: number,
  immediate = false,
) {
  const target = getCameraTarget(focusX, focusY, world, viewport);
  const blend = immediate ? 1 : 1 - Math.exp(-Math.max(0, dt) * 6);
  camera.x += (target.x - camera.x) * blend;
  camera.y += (target.y - camera.y) * blend;
  return camera;
}

export function screenToWorld(screenX: number, screenY: number, camera: CameraState) {
  return { x: screenX + camera.x, y: screenY + camera.y };
}

export function isWorldPointVisible(
  x: number,
  y: number,
  camera: CameraState,
  viewport: ViewportSize,
  padding = 0,
) {
  return x >= camera.x - padding
    && x <= camera.x + viewport.width + padding
    && y >= camera.y - padding
    && y <= camera.y + viewport.height + padding;
}
